import { announce, closeDialog, debounce, fetchSection, swapContent } from 'theme';

/**
 * Filtering and sorting for snippets/facets.liquid. A change to a filter or the sort order asks Shopify for the section with
 * the new parameters, then refreshes the products, the count, the active filters and the filter list in place. The address
 * bar follows, so the back button and shared links keep the filters.
 */
if (!customElements.get('facet-filters')) {
  customElements.define(
    'facet-filters',
    class extends HTMLElement {
      /** @type {AbortController|undefined} */
      #request;

      /** @returns {HTMLFormElement} */
      get #form() {
        return this.querySelector('form');
      }

      #formAddress() {
        const address = new URL(this.#form.getAttribute('action'), window.location.href);
        for (const [name, value] of new FormData(this.#form)) {
          if (value !== '') address.searchParams.append(name, String(value));
        }
        return address.href;
      }

      #onChange = (event) => {
        if (!(event.target instanceof Element) || !this.#form?.contains(event.target)) return;
        if (event.target.matches('input[type="number"]')) this.#applyPrice();
        else this.#apply(this.#formAddress());
      };

      #applyPrice = debounce(() => this.#apply(this.#formAddress()), 600);

      #onClick = (event) => {
        const link = event.target.closest('a.facets__chip, a.facets__clear, .facets__footer a, [data-facets-region] .pagination a');
        if (!link || !this.#pageHas(link)) return;
        event.preventDefault();
        this.#apply(link.href);
      };

      #onSubmit = (event) => {
        event.preventDefault();
        closeDialog(this.querySelector('dialog[open]'));
        this.#apply(this.#formAddress());
      };

      #onPopState = () => this.#apply(window.location.href, false);

      #pageHas(element) {
        return document.body.contains(element);
      }

      /**
       * @param {string} address - The page address with the filter and sort parameters
       * @param {boolean} [remember] - Add the address to the browser history
       */
      async #apply(address, remember = true) {
        this.#request?.abort();
        this.#request = new AbortController();
        const regions = [...document.querySelectorAll('[data-facets-region]')];
        for (const region of regions) region.classList.add('is-loading');

        // Open filter groups and the focused field are restored after the refresh.
        const openGroups = [...this.querySelectorAll('details[open]')].map((details) => details.id);
        const focusedId = document.activeElement?.id;

        try {
          const source = await fetchSection(address, this.dataset.sectionId, { signal: this.#request.signal });
          swapContent(
            source,
            regions.map((region) => region.id),
          );
          for (const id of openGroups) {
            const details = document.getElementById(id);
            if (details instanceof HTMLDetailsElement) details.open = true;
          }
          if (focusedId) document.getElementById(focusedId)?.focus();

          if (remember) {
            const visible = new URL(address);
            visible.searchParams.delete('section_id');
            window.history.pushState({ facets: true }, '', visible);
          }
          const count = this.querySelector('.facets__count') || document.querySelector('[data-facets-region] .search-page__summary');
          if (count) announce(count.textContent.trim());
        } catch (error) {
          if (error.name !== 'AbortError') window.location.assign(address);
        } finally {
          for (const region of document.querySelectorAll('[data-facets-region]')) region.classList.remove('is-loading');
        }
      }

      connectedCallback() {
        this.addEventListener('change', this.#onChange);
        document.addEventListener('click', this.#onClick);
        this.#form?.addEventListener('submit', this.#onSubmit);
        window.addEventListener('popstate', this.#onPopState);
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.#onChange);
        document.removeEventListener('click', this.#onClick);
        window.removeEventListener('popstate', this.#onPopState);
        this.#request?.abort();
      }
    },
  );
}
